import React, { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "./ReprintButton.css";

const API_BASE = "https://hms.backend.hiragroupcloud.com";

const ReprintButton = () => {
  const [searchParams] = useSearchParams();
  const [labInfo, setLabInfo] = useState(null);
  const [stampUrl, setStampUrl] = useState("");
  const [previewSaveError, setPreviewSaveError] = useState(null);

  const accountIds = useMemo(() => {
    const ids = searchParams.get("accountIds");
    return ids
      ? ids
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id))
      : [];
  }, [searchParams]);

  const providerIds = useMemo(() => {
    const ids = searchParams.get("providerIds");
    return ids ? ids.split(",").filter((id) => id && id.trim() !== "") : [];
  }, [searchParams]);

  const dateParam = searchParams.get("date");
  const lastRunTimeParam = searchParams.get("lastRunTime");
  const selectedDateParam = searchParams.get("selectedDate");
  const displayDate =
    selectedDateParam ||
    (dateParam
      ? new Date(dateParam).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0]);
  const cutoffDateTime = dateParam || new Date().toISOString();
  const lastRunTime = lastRunTimeParam || null;
  const statementNote = decodeURIComponent(
    searchParams.get("statementNote") || ""
  );
  const statementFooter = decodeURIComponent(
    searchParams.get("statementFooter") || ""
  );
  const dontPrintEbilling =
    searchParams.get("dontPrintEbilling") === "true";
  const dontPrintZeroBalance =
    searchParams.get("dontPrintZeroBalance") === "true";

  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const hideZeroBalanceInPrint = dontPrintZeroBalance === true;

  const removeLeadingZeros = (str) => {
    if (!str) return "";
    const result = str.replace(/^0+/, "");
    return result === "" ? "0" : result;
  };

  const formatAmountWithComma = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCredit = (amount) => {
    if (!amount || Number(amount) === 0) return "";
    return `-£${formatAmountWithComma(Math.abs(amount))}`;
  };

  const formatDebit = (amount) => {
    if (!amount && amount !== 0) return "£0.00";
    const num = Number(amount);
    if (num === 0) return "£0.00";
    return num < 0
      ? `-£${formatAmountWithComma(Math.abs(num))}`
      : `£${formatAmountWithComma(num)}`;
  };

  const formatBalance = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return "£0.00";
    return num < 0
      ? `-£${formatAmountWithComma(Math.abs(num))}`
      : `£${formatAmountWithComma(num)}`;
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ""} ${
        invoice.patientLastName || ""
      }`.trim();
    }
    return "";
  };

  const formatDisplayDate = (dateStr) => {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return "Invalid Date";
    const day = dateObj.getUTCDate().toString().padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${day} ${monthNames[dateObj.getUTCMonth()]} ${dateObj.getUTCFullYear()}`;
  };

  const savePreviewToDatabase = useCallback(
    async (statementsData) => {
      if (!statementsData || statementsData.length === 0) return;
      try {
        await axios.post(
          "https://hms.backend.hiragroupcloud.com/api/statements/preview/save",
          {
            accountIds: searchParams.get("accountIds") || "",
            providerIds: searchParams.get("providerIds") || "",
            date: cutoffDateTime,
            lastRunTime,
            dontPrintEbilling,
            dontPrintZeroBalance,
            statementNote,
            statementFooter,
            statements: statementsData,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (err) {
        console.error("Failed to save preview:", err);
        setPreviewSaveError(
          err.response?.data?.message || "Failed to save preview"
        );
        setTimeout(() => setPreviewSaveError(null), 5000);
      }
    },
    [
      searchParams,
      cutoffDateTime,
      lastRunTime,
      dontPrintEbilling,
      dontPrintZeroBalance,
      statementNote,
      statementFooter,
    ]
  );

  // -------------------------------------------------------------------------
  // Fetch the actual lab details for the lab whose statement is running.
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    const safeParse = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const buildLabInfoFromStorage = () => {
      // Check both activeLabId and userLabId for backward compatibility
      const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";
      
      // The data you provided seems to be stored either directly or under a key like "userData"
      const storedLabInfo = safeParse(localStorage.getItem("labInfo"));
      const storedLabDetails = safeParse(localStorage.getItem("labDetails"));
      const storedUserData = safeParse(localStorage.getItem("userData"));
      const storedAddress = safeParse(localStorage.getItem("userAddress"));
      
      const storedLabName = localStorage.getItem("labName") || "";
      const storedLabEmail =
        localStorage.getItem("labEmail") ||
        localStorage.getItem("userEmail") ||
        "";

      // Helper to extract fields safely from any of the stored objects
      const getField = (field) => {
        return (
          storedLabInfo?.[field] ||
          storedLabDetails?.[field] ||
          storedUserData?.[field] ||
          ""
        );
      };

      // Determine the best email: prefer labEmail, fallback to email, then statementEmail
      const resolvedEmail =
        getField("labEmail") ||
        storedLabEmail ||
        getField("email") ||
        getField("statementEmail") ||
        "";

      const merged = {
        labId: activeLabId,
        labName: getField("labName") || storedLabName || "",
        labEmail: resolvedEmail,
        address: getField("address") || storedAddress?.address || "",
        address2: getField("address2") || storedAddress?.address2 || "",
        city: getField("city") || storedAddress?.city || "",
        state: getField("state") || getField("stateProvince") || storedAddress?.state || "",
        zipCode: getField("zipCode") || getField("zipPostal") || storedAddress?.zipPostal || "",
        country: getField("country") || storedAddress?.country || "",
        telephone: getField("telephone") || "",
        // stamp (used as a fallback if localStorage "technicianStamp" is empty)
        technicianStamp:
          storedLabInfo?.technicianStamp ||
          storedLabInfo?.preferences?.technicianStamp ||
          storedUserData?.technicianStamp ||
          storedUserData?.preferences?.technicianStamp ||
          "",
      };

      const hasAny =
        merged.labName ||
        merged.labEmail ||
        merged.address ||
        merged.city ||
        merged.telephone;
      return hasAny ? merged : null;
    };

    const fetchLabInfo = async () => {
      const fromStorage = buildLabInfoFromStorage();
      if (fromStorage) {
        setLabInfo(fromStorage);
        return;
      }

      // Fallback to API
      try {
        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/auth/users",
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.data.success && response.data.users.length > 0) {
          const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";
          const matching =
            response.data.users.find(
              (u) =>
                String(u.labId || u.userLabId || "") === String(activeLabId)
            ) || response.data.users[0];
          setLabInfo(matching);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);

  // -------------------------------------------------------------------------
  // Technician stamp (same stamp uploaded in EditPreferences)
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    const resolveStamp = () => {
      const raw =
        localStorage.getItem("technicianStamp") ||
        labInfo?.technicianStamp ||
        labInfo?.preferences?.technicianStamp ||
        "";

      if (!raw) {
        setStampUrl("");
        return;
      }

      if (/^https?:\/\//i.test(raw)) {
        setStampUrl(raw);
        return;
      }

      let cleanPath = raw;
      if (!cleanPath.startsWith("/")) {
        cleanPath = `/uploads/stamps/${cleanPath}`;
      } else if (!cleanPath.startsWith("/uploads")) {
        cleanPath = `/uploads/stamps/${cleanPath.replace(/^\//, "")}`;
      }
      setStampUrl(`${API_BASE}${cleanPath}`);
    };

    resolveStamp();
    window.addEventListener("stampUpdated", resolveStamp);
    return () => window.removeEventListener("stampUpdated", resolveStamp);
  }, [labInfo]);

  React.useEffect(() => {
    const fetchStatements = async () => {
      if (accountIds.length === 0) {
        setError("No valid account IDs provided.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(
          "https://hms.backend.hiragroupcloud.com/api/statements/all",
          {
            params: {
              accountIds: accountIds.join(","),
              date: cutoffDateTime,
              lastRunTime,
              dontPrintEbilling,
              dontPrintZeroBalance,
              providerIds: providerIds.join(","),
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (
          response.data.success &&
          Array.isArray(response.data.statements)
        ) {
          setStatements(response.data.statements);
          await savePreviewToDatabase(response.data.statements);
        } else {
          setError(response.data.message || "No statements data available");
          setStatements([]);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching statements."
        );
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountIds,
    cutoffDateTime,
    lastRunTime,
    dontPrintEbilling,
    dontPrintZeroBalance,
    providerIds,
  ]);

  const displayStatements = useMemo(() => {
    const filtered =
      providerIds.length === 0
        ? statements
        : statements
            .map((statement) => {
              const filteredGrouped = {};
              Object.entries(statement.groupedByProvider || {}).forEach(
                ([key, group]) => {
                  if (
                    key === "general" ||
                    providerIds.includes(group.providerId)
                  ) {
                    filteredGrouped[key] = group;
                  }
                }
              );
              if (Object.keys(filteredGrouped).length === 0) return null;
              return { ...statement, groupedByProvider: filteredGrouped };
            })
            .filter(Boolean);

    return filtered.slice().sort((a, b) => {
      const nameA = (a.accountHolder || "Unknown").toLowerCase();
      const nameB = (b.accountHolder || "Unknown").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [statements, providerIds]);

  const handlePrintAndSave = async () => {
    if (displayStatements.length === 0) {
      setSaveMessage("No statements to save.");
      return;
    }

    try {
      setIsSaving(true);
      setSaveMessage("");

      const statementsToSave = [];
      const baseUrl = window.location.origin + "/run-date-range-statement";

      for (const statement of displayStatements) {
        const groups = Object.values(statement.groupedByProvider || {});
        if (groups.length === 0) continue;

        const queryParams = new URLSearchParams({
          accountIds: String(statement.accountId),
          date: cutoffDateTime,
          lastRunTime: lastRunTime || "",
          statementNote: encodeURIComponent(statementNote),
          statementFooter: encodeURIComponent(statementFooter),
          dontPrintEbilling: dontPrintEbilling.toString(),
          dontPrintZeroBalance: dontPrintZeroBalance.toString(),
          autoSaveStatements: "false",
        }).toString();

        const providerNamesCombined = groups
          .map((g) => g.providerName || "Unknown Provider")
          .filter((name, idx, arr) => arr.indexOf(name) === idx)
          .join(", ");

        const generalGroup = statement.groupedByProvider?.general;

        statementsToSave.push({
          runDate: cutoffDateTime,
          displayDate,
          accountId: String(statement.accountId),
          providerName: providerNamesCombined || "Unknown Provider",
          accountHolder: statement.accountHolder || "Unknown",
          address: statement.address || "",
          city: statement.city || "",
          state: statement.state || "",
          zip: statement.zip || "",
          telephone: statement.telephone || "Not available",
          email: statement.email || "Email not available",
          note: statementNote || "",
          footer: statementFooter || "",

          previousBalance: statement.previousBalance || 0,
          newBalance: statement.newBalance || 0,
          carriedForwardCredit: statement.carriedForwardCredit || 0,

          generalUnappliedPayment: generalGroup?.generalUnappliedPayment || 0,
          generalUnappliedCredit: generalGroup?.generalUnappliedCredit || 0,

          oneTo30Days: statement.oneTo30Days || 0,
          thirtyOneTo60Days: statement.thirtyOneTo60Days || 0,
          sixtyOneTo90Days: statement.sixtyOneTo90Days || 0,
          ninetyPlusDays: statement.ninetyPlusDays || 0,

          totalDebit: groups.reduce(
            (sum, g) => sum + Number(g.totalDebits || 0),
            0
          ),
          totalCredit: groups.reduce(
            (sum, g) => sum + Number(g.totalCredits || 0),
            0
          ),

          url: `${baseUrl}?${queryParams}`,
          invoices: groups.flatMap((g) => g.invoices || []),
          lastRunDate: statement.lastRunDate || null,

          groupedByProvider: statement.groupedByProvider || {},
        });
      }

      if (statementsToSave.length === 0) {
        setSaveMessage("No statements with non-zero balance to save.");
        window.print();
        return;
      }

      const response = await axios.post(
        "https://hms.backend.hiragroupcloud.com/api/statements/save",
        { runDate: cutoffDateTime, statements: statementsToSave },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSaveMessage(`Successfully saved ${response.data.count} statements.`);
      window.print();
    } catch (err) {
      console.error("Save error:", err);
      setSaveMessage(
        err.response?.data?.message || "Failed to save statements."
      );
      window.print();
    } finally {
      setIsSaving(false);
    }
  };

  const renderPreviewSaveStatus = () => {
    if (isLoading) return null;
    if (previewSaveError) {
      return (
        <span className="preview-save-error">
          ⚠ Preview not saved: {previewSaveError}
        </span>
      );
    }
    return null;
  };

  if (error) {
    return (
      <div className="reprint-statement-root">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="reprint-statement-root">
      {isLoading ? (
        <p>Loading statements...</p>
      ) : displayStatements.length === 0 ? (
        <div>
          <p>No statements available for the selected criteria.</p>
          {providerIds.length > 0 && (
            <p
              style={{ color: "#666", fontSize: "0.9rem", marginTop: "10px" }}
            >
              No invoices found for the selected providers.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="header-controls">
            <div className="button-container">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <button
                  className="print-button"
                  onClick={handlePrintAndSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Print"}
                </button>
                {renderPreviewSaveStatus()}
              </div>
              {saveMessage && <p className="save-message">{saveMessage}</p>}
            </div>
          </div>

          {displayStatements.map((statement, statementIndex) => {
            const providerList = Object.values(
              statement.groupedByProvider || {}
            )
              .filter(
                (group) =>
                  (group.invoices && group.invoices.length > 0) ||
                  (group.transactions && group.transactions.length > 0)
              )
              .sort((a, b) => {
                if (a.providerId === "general") return -1;
                if (b.providerId === "general") return 1;
                return (a.providerName || "").localeCompare(
                  b.providerName || ""
                );
              });

            return (
              <div
                key={statement.accountId}
                className={
                  hideZeroBalanceInPrint &&
                  Number(statement.newBalance || 0) === 0 &&
                  Number(statement.previousBalance || 0) === 0 &&
                  !statement.carriedForwardCredit
                    ? "hide-in-print"
                    : ""
                }
                style={{
                  marginBottom: "40px",
                  pageBreakBefore: statementIndex > 0 ? "always" : "auto",
                }}
              >
                <div className="preview-watermark">
                  Preview <br /> Only
                </div>

                <div className="header-section">
                  <div className="lab-info">
                    <strong>{labInfo?.labName || "Hira Dental Laboratory"}</strong>
                    <br />
                    {labInfo?.address || "02 Ulverscroft Road"}
                    <br />
                    {[
                      labInfo?.city,
                      labInfo?.state,
                      labInfo?.zipCode || labInfo?.zipPostal,
                    ]
                      .filter(Boolean)
                      .join(", ") || "Coventry, CV3 5EZ"}
                    <br />
                    {labInfo?.telephone
                      ? `Tel: ${labInfo.telephone}`
                      : "Tel: 024 7642 2500"}
                    <br />
                  </div>
                  <div className="header-right">
                    <div className="statement-info">
                      <div className="statement-date">
                        <strong>
                          Statement for {formatDisplayDate(displayDate)}
                        </strong>
                      </div>
                      <div className="contact-info">
                        {labInfo?.labEmail || "info@hiradentallab.com"}
                      </div>
                    </div>
                    <div className="logo-container-right">
                      {stampUrl && (
                        <img
                          className="header-stamp"
                          src={stampUrl}
                          alt="Technician Stamp"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="statement-title">
                  Statement for {formatDisplayDate(displayDate)}
                </h3>

                <div className="client-payment-section">
                  <div className="client-info">
                    <strong>
                      {statement.accountHolder || "Account Holder Name"}
                    </strong>
                    <br />
                    {statement.address &&
                    statement.address !== "Address not available"
                      ? statement.address
                      : "02 Ulverscroft Road"}
                    <br />
                    {[statement.city, statement.state, statement.zip]
                      .filter(Boolean)
                      .join(", ") || "Coventry, CV4 5EZ"}
                    <br />
                    {statement.telephone &&
                    statement.telephone !== "Not available"
                      ? `Tel: ${statement.telephone}`
                      : "Tel: 024 7642 2500"}
                    <br />
                    {statement.email &&
                    statement.email !== "Email not available"
                      ? `Email: ${statement.email}`
                      : "Email: accounts@hiradentallab.com"}
                  </div>
                  <div className="payment-details">
                    {statementNote ? (
                      <div className="payment-details-text">
                        {statementNote}
                      </div>
                    ) : (
                      <div>
                        <div>Please remit payment to:</div>
                        <div>Hira Dental Laboratory</div>
                        <div>Sort Code: 20-00-00</div>
                        <div>Account No: 12345678</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providerList.map((provider) => {
                        const lines = [];

                        const prevInvoices = (provider.invoices || []).filter(
                          (inv) => inv.isPreviousBalance
                        );
                        const currentInvoices = (
                          provider.invoices || []
                        ).filter((inv) => !inv.isPreviousBalance);

                        if (prevInvoices.length > 0) {
                          const prevTotal = prevInvoices.reduce(
                            (sum, inv) => sum + (inv.debit || 0),
                            0
                          );
                          const prevDateStr = statement.lastRunDate || "";
                          lines.push({
                            dateStr: formatDisplayDate(prevDateStr),
                            desc: "Previous Balance",
                            debit: formatDebit(prevTotal),
                            credit: "",
                            sortDate: new Date(prevDateStr || "1970-01-01"),
                          });
                        }

                        currentInvoices.forEach((invoice) => {
                          const patientName = getPatientName(invoice);
                          const invoiceDate =
                            invoice.deliveryDate || invoice.invoiceDate;
                          const formattedInvoiceNumber =
                            invoice.formattedInvoiceNumber ||
                            removeLeadingZeros(invoice.invoiceNumber);
                          lines.push({
                            dateStr: formatDisplayDate(invoiceDate),
                            desc: `Case Invoice ${formattedInvoiceNumber} / ${patientName}`,
                            debit: formatDebit(invoice.debit || 0),
                            credit: "",
                            sortDate: new Date(invoiceDate),
                          });
                        });

                        (provider.transactions || []).forEach(
                          (trans, idx) => {
                            lines.push({
                              dateStr: formatDisplayDate(trans.date),
                              desc: trans.displayDescription,
                              debit: "",
                              credit: formatCredit(trans.amount),
                              sortDate: new Date(trans.date),
                              seq:
                                typeof trans.seq === "number"
                                  ? trans.seq + 1
                                  : idx + 1,
                            });
                          }
                        );
                        lines.sort((a, b) => {
                          const diff = a.sortDate - b.sortDate;
                          return diff !== 0
                            ? diff
                            : (a.seq || 0) - (b.seq || 0);
                        });

                        const displayProviderName =
                          provider.providerId === "general"
                            ? "General"
                            : provider.providerName;

                        return (
                          <React.Fragment key={provider.providerId}>
                            <tr className="provider-row">
                              <td colSpan="4">
                                <strong>{displayProviderName}</strong>
                              </td>
                            </tr>
                            {lines.length > 0 ? (
                              lines.map((line, lineIdx) => (
                                <tr
                                  key={`${provider.providerId}-line-${lineIdx}`}
                                >
                                  <td>{line.dateStr}</td>
                                  <td>{line.desc}</td>
                                  <td>{line.debit}</td>
                                  <td>{line.credit}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="no-transactions"
                                >
                                  No transactions for this provider
                                </td>
                              </tr>
                            )}
                            {providerList.length > 1 && (
                              <tr className="subtotal-row">
                                <td colSpan="2">
                                  <strong>Subtotal</strong>
                                </td>
                                {provider.providerId === "general" ? (
                                  <>
                                    <td></td>
                                    <td>
                                      {formatCredit(provider.newBalance || 0)}
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>
                                      {formatDebit(provider.newBalance || 0)}
                                    </td>
                                    <td></td>
                                  </>
                                )}
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="table-wrapper">
                  <table className="statement-table center">
                    <thead>
                      <tr>
                        <th>1-30 Days</th>
                        <th>31-60 Days</th>
                        <th>61-90 Days</th>
                        <th>91+ Days</th>
                        <th>New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{formatBalance(statement.oneTo30Days)}</td>
                        <td>{formatBalance(statement.thirtyOneTo60Days)}</td>
                        <td>{formatBalance(statement.sixtyOneTo90Days)}</td>
                        <td>{formatBalance(statement.ninetyPlusDays)}</td>
                        <td>{formatBalance(statement.newBalance)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="statement-footer">
                  <div>{statement.accountHolder}</div>
                  <div>
                    {statementFooter || "Thank you for your business"}
                  </div>
                  <div>Page {statementIndex + 1}</div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ReprintButton;